import { g as getRpcPathFromStrings, a as absolutize } from '../get-rpc-path-D_Y_XvCI.mjs';
import { R as RpcLoggerFactory, C as ConsoleRequestLogger, g as generateSpeleoId, a as ConnectError, p as parseResponse } from '../trace-id-O1EJH_rA.mjs';

function defaultUrl(derivedUrl, _, __) {
  return derivedUrl;
}
function requestImpl({
  urlForMethod,
  additionalHeaders,
  sendSpeleoTraceId,
  signal
}) {
  return async (service, method, data) => {
    const url = urlForMethod(
      getRpcPathFromStrings(service, method),
      service,
      method
    );
    const headers = {};
    headers.Accept = "application/x-protobuf";
    headers["Content-Type"] = "application/x-protobuf";
    for (const [key, value] of (additionalHeaders instanceof Function ? additionalHeaders() : additionalHeaders).entries()) {
      headers[key] = value;
    }
    if (sendSpeleoTraceId && !("X-SPELEO-TRACE-ID" in headers)) {
      headers["X-SPELEO-TRACE-ID"] = generateSpeleoId();
    }
    const fetchOptions = {
      method: "POST",
      credentials: "include",
      headers,
      body: data,
      signal
    };
    const response = await fetch(absolutize(url), fetchOptions);
    const responseContentType = response.headers.get("content-type")?.split(";")[0].trim();
    if (responseContentType !== "application/x-protobuf") {
      const text = await response.clone().text();
      return Promise.reject(new ConnectError(response, null, text));
    }
    if (response.status >= 400) {
      return Promise.reject(new ConnectError(response, null, "Protobuf-encoded error response received"));
    }
    return parseResponse(response);
  };
}
function beforeRequestImpl({
  urlForMethod,
  logger,
  beforeRequest
}) {
  return (service, method, request) => {
    const url = urlForMethod(
      getRpcPathFromStrings(service, method),
      service,
      method
    );
    logger.logRequest(url, request);
    if (beforeRequest) {
      beforeRequest(request, service, method);
    }
  };
}
function handleErrorImpl({
  urlForMethod,
  logger,
  handleError
}) {
  return (service, method, error) => {
    const url = urlForMethod(
      getRpcPathFromStrings(service, method),
      service,
      method
    );
    logger.logError(url, error);
    const responseError = new Error(
      `${error.message} 
 Proto: ${method} 
 URL: ${url}`,
      // remove \`@ts-expect-error` once jest supports ES2022
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      { cause: error }
    );
    if (handleError) {
      const modifiedError = handleError(error, service, method);
      if (modifiedError) {
        return modifiedError;
      }
    }
    return responseError;
  };
}
function afterResponseImpl({
  urlForMethod,
  logger,
  afterResponse
}) {
  return (service, method, response) => {
    const url = urlForMethod(
      getRpcPathFromStrings(service, method),
      service,
      method
    );
    logger.logResponse(url, response);
    if (afterResponse) {
      afterResponse(response, service, method);
    }
  };
}
function rpc({
  urlForMethod = defaultUrl,
  loggerFactory = new RpcLoggerFactory(ConsoleRequestLogger),
  additionalHeaders = /* @__PURE__ */ new Map(),
  sendSpeleoTraceId = false,
  beforeRequest,
  afterResponse,
  handleError,
  signal
}) {
  const logger = loggerFactory.build();
  return {
    request: requestImpl({
      urlForMethod,
      additionalHeaders,
      sendSpeleoTraceId,
      signal
    }),
    afterResponse: afterResponseImpl({ urlForMethod, logger, afterResponse }),
    beforeRequest: beforeRequestImpl({ urlForMethod, logger, beforeRequest }),
    handleError: handleErrorImpl({ urlForMethod, logger, handleError })
  };
}

export { afterResponseImpl, beforeRequestImpl, rpc as default, defaultUrl, handleErrorImpl, requestImpl };
